import React from "react";
import Typography from "@pagepro-monorepo/ui/lib/components/styles/Typography";
import Button from "@pagepro-monorepo/ui/lib/components/atoms/Button";

import IconArrowLeft from "@assets/svg/arrow-left.svg";
import IconArrowRight from "@assets/svg/arrow-right.svg";

import { getBlocks } from "./assessment";
import { BLOCK_ICONS } from "./icons";
import { Question, QuestionId, Answers } from "./types";
import * as Styled from "./styles";

interface QuestionsPhaseProps {
  currentStep: number;
  totalSteps: number;
  currentQuestion: Question;
  answers: Answers;
  onAnswer: (questionId: QuestionId, value: string) => void;
  onNavigate: (direction: "prev" | "next" | "finish") => void;
}

const blocks = getBlocks();

const QuestionsPhase: React.FC<QuestionsPhaseProps> = ({
  currentStep,
  totalSteps,
  currentQuestion,
  answers,
  onAnswer,
  onNavigate
}) => {
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const selectedValue = answers[currentQuestion.id];
  const hasAnswer = selectedValue !== undefined;
  const isLastQuestion = currentStep === totalSteps - 1;

  return (
    <div>
      {/* Block pills */}
      <Styled.PillScroller>
        {blocks.map((block) => (
          <Styled.BlockPill
            key={block.key}
            $isActive={block.key === currentQuestion.block}
          >
            {BLOCK_ICONS[block.key] &&
              React.createElement(BLOCK_ICONS[block.key])}
            <Styled.BlockPillText>{block.label}</Styled.BlockPillText>
          </Styled.BlockPill>
        ))}
      </Styled.PillScroller>

      {/* Progress bar */}
      <Styled.ProgressBar>
        <span style={{ width: `${progress}%` }} />
      </Styled.ProgressBar>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "0.5rem",
          marginBottom: "2rem"
        }}
      >
        <Styled.Footnote>
          Question {currentStep + 1} of {totalSteps}
        </Styled.Footnote>
        <Styled.Footnote>
          {Math.round(progress)}% complete
        </Styled.Footnote>
      </div>

      {/* Question */}
      <div style={{ marginBottom: "1.5rem" }}>
        <Typography variant="subheading2" as="h2" mb="1.5rem">
          {currentQuestion.question}
        </Typography>

        <div
          role="radiogroup"
          aria-label={currentQuestion.question}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          {currentQuestion.options.map((option) => (
            <Styled.OptionButton
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selectedValue === option.value}
              tabIndex={0}
              $isSelected={selectedValue === option.value}
              onClick={() =>
                onAnswer(currentQuestion.id, option.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAnswer(currentQuestion.id, option.value);
                }
              }}
            >
              {option.label}
            </Styled.OptionButton>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <Styled.NavigationBar>
        <div>
          {currentStep > 0 && (
            <Button
              type="button"
              $mode="secondary"
              onClick={() => onNavigate("prev")}
            >
              <IconArrowLeft
                style={{
                  fill: "currentColor",
                  marginRight: "0.5rem"
                }}
              />
              Back
            </Button>
          )}
        </div>
        <div>
          {isLastQuestion ? (
            <Button
              type="button"
              $mode="secondary"
              onClick={() => onNavigate("finish")}
              disabled={!hasAnswer}
              icon={IconArrowRight}
            >
              See Results
            </Button>
          ) : (
            <Button
              type="button"
              $mode="secondary"
              onClick={() => onNavigate("next")}
              disabled={!hasAnswer}
              icon={IconArrowRight}
            >
              Next
            </Button>
          )}
        </div>
      </Styled.NavigationBar>
    </div>
  );
};

export default QuestionsPhase;
